<script>
import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: "TableFilterDropdown",
  props: {
    column: { type: Object, required: true },
    isOpen: { type: Boolean, default: false }
  },
  data() {
    return {
      dropdownStyle: {},
      comparisons: { gte: "", lte: "", gt: "", lt: "" }
    };
  },
  computed: {
    ...mapGetters('gsheet', ['getActiveFilters']),
    activeFilters() {
      console.log('filters', this.getActiveFilters)
      return this.getActiveFilters;
    },
    currentFilters() {
      return this.activeFilters.filter(f => f.column === this.column.name);
    },
    searchQueryLocal: {
      get() {
        const f = this.currentFilters.find(f => f.type === 'search');
        return f ? f.value : '';
      },
      set(value) {
        const filter = { type: 'search', column: this.column.name, value };
        this.addFilter(filter);
      }
    },
    sortOrderLocal: {
      get() {
        const f = this.currentFilters.find(f => f.type === 'sort');
        return f ? f.value : null;
      },
      set(value) {
        const filter = { type: 'sort', column: this.column.name, value };
        this.addFilter(filter);
      }
    },
    selectedStatusLocal: {
      get() {
        const f = this.currentFilters.find(f => f.type === 'status');
        return f ? f.value : null;
      },
      set(value) {
        const filter = { type: 'status', column: this.column.name, value };
        this.addFilter(filter);
      }
    }
  },
  methods: {
    ...mapMutations('gsheet', ['addFilter', 'removeFilter', 'clearFilters']),
    
    toggle() {
      this.$emit("toggle", this.column.name);
      if (this.isOpen) this.$nextTick(() => this.updatePosition());
    },

    handleSearch() {
      this.searchQueryLocal = this.searchQueryLocal;
      this.$emit("action", { column: this.column, action: "search", value: this.searchQueryLocal });
      this.$emit("close");
    },
    clearSearch() {
      const f = this.currentFilters.find(f => f.type === 'search');
      if (f) this.removeFilter(f);
      this.$emit("action", { column: this.column, action: "clearSearch" });
    },

    handleStatus(status) {
      this.selectedStatusLocal = status;
      this.$emit("action", { column: this.column, action: "status", value: status });
      this.$emit("close");
    },
    clearStatus() {
      const f = this.currentFilters.find(f => f.type === 'status');
      if (f) this.removeFilter(f);
      this.$emit("action", { column: this.column, action: "clearStatus" });
    },

    handleSort(order) {
      this.sortOrderLocal = order;
      this.$emit("action", { column: this.column, action: "sort", value: order });
      this.$emit("close");
    },
    clearSort() {
      const f = this.currentFilters.find(f => f.type === 'sort');
      if (f) this.removeFilter(f);
      this.$emit("action", { column: this.column, action: "clearSort" });
    },

    handleComparison(op) {
      const filter = { type: op, column: this.column.name, value: this.comparisons[op] };
      this.addFilter(filter);
      this.$emit("action", { column: this.column, action: op, value: this.comparisons[op] });
      this.$emit("close");
    },
    clearComparison(op) {
      this.comparisons[op] = "";
      const f = this.currentFilters.find(f => f.type === op);
      if (f) this.removeFilter(f);
      this.$emit("action", { column: this.column, action: "clearComparison", value: op });
    },

    handleAggregation(agg) {
      this.$emit("action", { column: this.column, action: agg });
      this.$emit("close");
    },

    clearFilter() {
      // Clear numeric comparisons
      this.comparisons = { gte: "", lte: "", gt: "", lt: "" };

      // Remove all filters for this column
      this.currentFilters.forEach(f => this.removeFilter(f));

      this.$emit("action", { column: this.column, action: "clearAll" });
      this.$emit("close");
    },

    clearAllFilters(){
      this.clearFilters();
      this.$emit("close");
    },

    async updatePosition() {
      const trigger = this.$refs.trigger;
      const dropdown = this.$refs.dropdown;
      if (!trigger || !dropdown) return;

      const { x, y } = await computePosition(trigger, dropdown, {
        placement: 'bottom-start',
        middleware: [offset(6), flip(), shift({ padding: 8 })],
      });

      this.dropdownStyle = { left: `${x}px`, top: `${y}px` };
    },

    handleClickOutside(e) {
      const trigger = this.$refs.trigger;
      const dropdown = this.$refs.dropdown;
      if (this.isOpen && !trigger.contains(e.target) && dropdown && !dropdown.contains(e.target)) {
        this.$emit("close");
      }
    }
  },

  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => requestAnimationFrame(() => this.updatePosition()));
        document.addEventListener("click", this.handleClickOutside);
        window.addEventListener("scroll", this.updatePosition, true);
        window.addEventListener("resize", this.updatePosition);
      } else {
        document.removeEventListener("click", this.handleClickOutside);
        window.removeEventListener("scroll", this.updatePosition, true);
        window.removeEventListener("resize", this.updatePosition);
      }
    }
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.updatePosition, true);
    window.removeEventListener("resize", this.updatePosition);
  }
};
</script>


<template>
  <div class="relative inline-block">
    <!-- Trigger -->
    <button
      class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none"
      @click.stop="toggle"
      ref="trigger"
      aria-label="Open filter dropdown"
    >
      ⋮
    </button>

    <portal to="dropdowns">
      <div
        v-if="isOpen"
        ref="dropdown"
        class="absolute w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 p-3 space-y-4"
        :style="dropdownStyle"
      >
        <!-- Search -->
        <div v-if="column.supports.includes('search') && column.final_type === 'text'">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-300 mb-1">Search</h4>
          <input
            v-model="searchQueryLocal"
            @keyup.enter="handleSearch"
            type="text"
            placeholder="Search..."
            aria-label="Search input"
            class="w-full px-2 py-1 text-sm border rounded dark:bg-slate-800 dark:text-white border-slate-300 dark:border-slate-600"
          />
          <div class="flex gap-2 mt-1">
            <button
              class="flex-1 text-xs px-2 py-1 rounded bg-woot-500 text-white hover:bg-woot-600"
              aria-label="Apply search"
              @click="handleSearch"
            >
              Apply
            </button>
            <button
              v-if="searchQueryLocal"
              class="flex-1 text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700"
              aria-label="Clear search"
              @click="clearSearch"
            >
              Clear
            </button>
          </div>
        </div>

        <!-- Numeric Comparisons -->
        <!-- <div v-if="['integer','number'].includes(column.final_type)">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-300 mb-1">Numeric Filters</h4>
          <div
            v-for="op in ['gte','lte','gt','lt']"
            v-if="column.supports.includes(op)"
            :key="op"
            class="flex items-center gap-2 mb-1"
          >
            <label class="w-10 text-xs uppercase text-slate-500 dark:text-slate-400">{{ op }}</label>
            <input
              v-model="comparisons[op]"
              type="number"
              class="flex-1 px-2 py-1 text-sm border rounded dark:bg-slate-800 dark:text-white border-slate-300 dark:border-slate-600"
              @keyup.enter="handleComparison(op)"
              :aria-label="`Enter ${op} value`"
            />
            <button
              class="text-xs px-2 py-1 rounded bg-woot-500 text-white hover:bg-woot-600"
              aria-label="Apply comparison"
              @click="handleComparison(op)"
            >
              Apply
            </button>
            <button
              v-if="comparisons[op]"
              class="text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700"
              aria-label="Clear comparison"
              @click="clearComparison(op)"
            >
              ✕
            </button>
          </div>
        </div> -->

        <!-- Sort -->
        <div v-if="column.supports.includes('sort')">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-300 mb-1">Sort</h4>
          <div class="flex gap-2">
            <button
              class="flex-1 text-xs px-2 py-1 rounded text-white"
              :class="sortOrderLocal === 'asc'
                ? 'bg-green-500'
                : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'"
              aria-label="Sort ascending"
              @click="handleSort('asc')"
            >
              ↑ Asc
            </button>
            <button
              class="flex-1 text-xs px-2 py-1 rounded text-white"
              :class="sortOrderLocal === 'desc'
                ? 'bg-green-500'
                : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'"
              aria-label="Sort descending"
              @click="handleSort('desc')"
            >
              ↓ Desc
            </button>
          </div>
          <button
            v-if="sortOrderLocal"
            class="mt-1 w-full text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700"
            aria-label="Clear sort"
            @click="clearSort"
          >
            Clear Sort
          </button>
        </div>

        <!-- Status -->
        <div v-if="column.name === 'is_enabled'">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-300 mb-1">Status</h4>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="status in [true, false]"
              :key="status"
              :class="[
                'text-xs px-2 py-1 rounded text-white',
                selectedStatusLocal === status
                  ? 'bg-green-500'
                  : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600'
              ]"
              :aria-label="status ? 'Enabled' : 'Disabled'"
              @click="handleStatus(status)"
            >
              {{ status ? 'Enabled' : 'Disabled' }}
            </button>
          </div>
          <!-- <button
            v-if="selectedStatus !== null"
            class="mt-1 w-full text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-700"
            aria-label="Clear status"
            @click="clearStatus"
          >
            Clear Status
          </button> -->
        </div>

        <!-- Aggregations -->
        <!-- <div v-if="['sum','avg','min','max'].some(a => column.supports.includes(a))">
          <h4 class="text-xs font-semibold text-slate-500 dark:text-slate-300 mb-1">Aggregations</h4>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="agg in ['sum','avg','min','max']"
              v-if="column.supports.includes(agg)"
              :key="agg"
              class="text-xs px-2 py-1 rounded bg-woot-500 text-white hover:bg-woot-600"
              :aria-label="`Apply ${agg}`"
              @click="handleAggregation(agg)"
            >
              {{ agg.toUpperCase() }}
            </button>
          </div>
        </div> -->

        <!-- Clear All -->
        <div>
          <button
            class="w-full text-xs px-2 py-1 rounded bg-red-200 dark:bg-red-700 text-red-700 dark:text-red-100 hover:bg-red-300 dark:hover:bg-red-600"
            aria-label="Clear all filters"
            @click="clearAllFilters"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </portal>
  </div>
</template>
